import jsPDF from "jspdf";
import type { CachedChatMessage } from "./notionChatHistory";

type ExportOptions = {
  title: string;
  subtitle?: string;
  studentName?: string;
  messages: CachedChatMessage[];
};

export type PdfDownloadResult = "saved" | "download-started" | false;

type SavePickerWindow = Window & {
  showSaveFilePicker?: (options?: {
    suggestedName?: string;
    types?: Array<{ description: string; accept: Record<string, string[]> }>;
  }) => Promise<{
    createWritable: () => Promise<{ write: (data: Blob) => Promise<void>; close: () => Promise<void> }>;
  }>;
};

// Strip blocs internes type <kelasai_meta> ou <think>
const stripInternal = (s: string) =>
  s
    .replace(/<kelasai_meta>[\s\S]*?<\/kelasai_meta>/gi, "")
    .replace(/<think>[\s\S]*?<\/think>/gi, "")
    .replace(/<think>[\s\S]*$/i, "")
    .trim();

// Conversion Markdown léger -> texte plat lisible
const mdToPlain = (s: string) =>
  s
    .replace(/\r\n/g, "\n")
    .replace(/```[\s\S]*?```/g, (block) => block.replace(/```\w*\n?/g, "").replace(/```/g, ""))
    .replace(/`([^`]+)`/g, "$1")
    .replace(/\*\*([^*]+)\*\*/g, "$1")
    .replace(/\*([^*]+)\*/g, "$1")
    .replace(/^#{1,6}\s*/gm, "")
    .replace(/!\[[^\]]*\]\([^)]+\)/g, "")
    .replace(/\[([^\]]+)\]\(([^)]+)\)/g, "$1 ($2)")
    .replace(/\n{3,}/g, "\n\n")
    .trim();

const sanitizeFilename = (s: string) =>
  s.normalize("NFD").replace(/[\u0300-\u036f]/g, "").replace(/[^a-zA-Z0-9_-]+/g, "_").slice(0, 60) || "chat";

export const downloadChatAsPdf = async ({ title, subtitle, studentName, messages }: ExportOptions): Promise<PdfDownloadResult> => {
  const printableMessages = messages
    .map((m) => ({ ...m, content: mdToPlain(stripInternal(m.content)) }))
    .filter((m) => m.content.length > 0);

  if (printableMessages.length === 0) return false;

  const doc = new jsPDF({ unit: "pt", format: "a4" });
  const pageWidth = doc.internal.pageSize.getWidth();
  const pageHeight = doc.internal.pageSize.getHeight();
  const margin = 48;
  const contentWidth = pageWidth - margin * 2;
  let y = margin;

  const ensureSpace = (needed: number) => {
    if (y + needed > pageHeight - margin) {
      doc.addPage();
      y = margin;
    }
  };

  // En-tête
  doc.setFont("helvetica", "bold");
  doc.setFontSize(18);
  doc.setTextColor(20, 20, 20);
  const titleLines = doc.splitTextToSize(title, contentWidth);
  doc.text(titleLines, margin, y);
  y += titleLines.length * 22;

  if (subtitle) {
    doc.setFont("helvetica", "normal");
    doc.setFontSize(11);
    doc.setTextColor(110, 110, 110);
    const subLines = doc.splitTextToSize(subtitle, contentWidth);
    doc.text(subLines, margin, y);
    y += subLines.length * 14;
  }

  doc.setFontSize(9);
  doc.setTextColor(140, 140, 140);
  const meta = `Exporté le ${new Date().toLocaleString("fr-FR")}${studentName ? ` · ${studentName}` : ""} · KelasAI`;
  doc.text(meta, margin, y);
  y += 16;

  doc.setDrawColor(220, 220, 220);
  doc.line(margin, y, pageWidth - margin, y);
  y += 18;

  // Messages
  printableMessages.forEach((m, idx) => {
    const isUser = m.role === "user";
    const label = isUser ? (studentName || "Élève") : "KelasAI";

    ensureSpace(28);
    doc.setFont("helvetica", "bold");
    doc.setFontSize(11);
    doc.setTextColor(isUser ? 30 : 50, isUser ? 80 : 100, isUser ? 180 : 200);
    doc.text(label, margin, y);
    y += 14;

    doc.setFont("helvetica", "normal");
    doc.setFontSize(11);
    doc.setTextColor(40, 40, 40);
    const lines = doc.splitTextToSize(m.content, contentWidth);
    lines.forEach((line: string) => {
      ensureSpace(15);
      doc.text(line, margin, y);
      y += 14;
    });

    y += 10;
    if (idx < printableMessages.length - 1) {
      ensureSpace(10);
      doc.setDrawColor(240, 240, 240);
      doc.line(margin, y, pageWidth - margin, y);
      y += 10;
    }
  });

  // Numéros de page
  const pageCount = doc.getNumberOfPages();
  for (let i = 1; i <= pageCount; i += 1) {
    doc.setPage(i);
    doc.setFont("helvetica", "normal");
    doc.setFontSize(9);
    doc.setTextColor(160, 160, 160);
    doc.text(`Page ${i} / ${pageCount}`, pageWidth - margin, pageHeight - 20, { align: "right" });
  }

  const blob = doc.output("blob");
  if (!blob || blob.size === 0) return false;

  const filename = `${sanitizeFilename(title)}.pdf`;
  const savePicker = (window as SavePickerWindow).showSaveFilePicker;

  if (savePicker) {
    try {
      const handle = await savePicker({
        suggestedName: filename,
        types: [{ description: "PDF", accept: { "application/pdf": [".pdf"] } }],
      });
      const writable = await handle.createWritable();
      await writable.write(blob);
      await writable.close();
      return "saved";
    } catch (error) {
      if ((error as DOMException)?.name === "AbortError") return false;
      console.warn("File picker unavailable, falling back to browser download", error);
    }
  }

  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  window.setTimeout(() => {
    link.remove();
    URL.revokeObjectURL(url);
  }, 1000);

  return "download-started";
};
